import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { actionLabel, Field } from './VaultForms'
import {
  acceptanceReferences,
  type AcceptanceReferencePage,
  future,
  id,
  object,
  readOffer,
  type Offer,
} from '@/services/legalControl/workflow'
import { useWorkflow } from '@/services/legalControl/useWorkflow'
import { WorkflowHistory } from '@/components/legalControl/WorkflowHistory'

function ConsentDetails() {
  const { user } = useAuth(),
    flow = useWorkflow()
  const [review, setReview] = useState(''),
    [offer, setOffer] = useState<Offer | null>(null),
    [expiry, setExpiry] = useState(''),
    [ack, setAck] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false)
  const [records, setRecords] = useState<AcceptanceReferencePage | null>(null)
  const current = useRef(''),
    generation = useRef(0)
  const identity = `${user?.id}:${user?.authScope}:${user?.tenantId}`
  current.current = identity
  useEffect(() => {
    generation.current++
    setOffer(null)
    setRecords(null)
    setAck(false)
    setError('')
    setBusy(false)
    setExpiry('')
  }, [review, identity])
  useEffect(
    () => () => {
      generation.current++
    },
    []
  )
  if (!user || user.authScope !== 'client_vault' || user.tenantId) return null
  async function load() {
    if (!user || busy) return
    const owner = identity,
      version = ++generation.current
    setBusy(true)
    setOffer(null)
    setAck(false)
    setError('')
    try {
      const value = await readOffer(id(review), user.id)
      if (owner === current.current && version === generation.current) setOffer(value)
    } catch {
      if (owner === current.current && version === generation.current)
        setError(
          'Reviewed terms are unavailable for this account. Check the review reference and your current account session.'
        )
    } finally {
      if (owner === current.current && version === generation.current) setBusy(false)
    }
  }
  async function findRecords(after?: string) {
    if (busy) return
    const owner = identity,
      version = ++generation.current
    setBusy(true)
    setError('')
    setRecords(null)
    try {
      const page = await acceptanceReferences(id(review), after)
      if (owner === current.current && version === generation.current) setRecords(page)
    } catch {
      if (owner === current.current && version === generation.current)
        setError(
          'Acceptance history is unavailable for this account and review. An unavailable result does not prove an earlier request had no effect.'
        )
    } finally {
      if (owner === current.current && version === generation.current) setBusy(false)
    }
  }
  let consent: Record<string, unknown> | null = null
  if (flow.view?.consent) {
    try {
      consent = object(flow.view.consent)
    } catch {
      consent = null
    }
  }
  const expired = offer && !future(offer.expires_at)
  return (
    <section className="card p-5 space-y-4" aria-label="Reviewed appointment request">
      <h2 className="text-xl font-semibold">Review an appointment request</h2>
      <p>
        Check the exact subject, requested role, capacity, actions and duration before accepting.
        Acceptance records your consent; verification must finish before an appointment becomes
        active.
      </p>
      <Field label="Review reference" value={review} onChange={e => setReview(e.target.value)} />
      <button className="btn" disabled={busy || !review} onClick={() => void load()}>
        Read reviewed terms
      </button>
      <button className="btn" disabled={busy || !review} onClick={() => void findRecords()}>
        Find my recorded acceptances
      </button>
      {records && (
        <section aria-label="Recorded acceptance references" className="space-y-3">
          {!records.items.length && (
            <p>
              No recorded acceptance is visible for this review. Keep any original pending command
              reference.
            </p>
          )}
          {records.items.map(row => (
            <div className="border rounded p-3" key={row.acceptance_id}>
              <p className="break-all">Acceptance: {row.acceptance_id}</p>
              <p>Recorded {new Date(row.accepted_at).toLocaleString()}</p>
              <button
                className="btn"
                disabled={flow.busy}
                onClick={() =>
                  void flow.run({
                    kind: 'accept',
                    target: records.review_id,
                    command: row.command_id,
                    status: 'pending',
                  })
                }
              >
                Read this acceptance
              </button>
            </div>
          ))}
          {records.next_cursor && (
            <button
              className="btn"
              disabled={busy}
              onClick={() => void findRecords(records.next_cursor!)}
            >
              Older acceptances
            </button>
          )}
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      {offer && (
        <div className="border rounded p-4 space-y-3">
          <p>Subject type: {offer.terms.subject_type === 'HUF' ? 'HUF' : 'Business'}</p>
          <p className="break-all">Subject reference: {offer.terms.subject_id}</p>
          <p>
            Requested role:{' '}
            {offer.terms.requested_role === 'legal_controller'
              ? 'Legal controller'
              : 'Representative'}
          </p>
          <p>Capacity: {offer.terms.declared_capacity}</p>
          <p>Appointment duration: {offer.terms.authority_seconds} seconds</p>
          <ul>
            {offer.terms.requested_actions.map(a => (
              <li key={a}>{actionLabel(a)}</li>
            ))}
          </ul>
          <p>Review valid until {new Date(offer.expires_at).toLocaleString()}</p>
          <Field
            label="Consent valid until"
            type="datetime-local"
            value={expiry}
            onChange={e => {
              setExpiry(e.target.value)
              setAck(false)
            }}
          />
          <label className="flex gap-2">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />I have
            reviewed these exact terms and consent to this appointment request.
          </label>
          <button
            className="btn btn-primary"
            disabled={flow.locked || !ack || !!expired || !expiry}
            onClick={() => {
              const end = new Date(expiry)
              if (
                !Number.isFinite(end.getTime()) ||
                end.getTime() <= Date.now() ||
                end.getTime() > Date.parse(offer.expires_at)
              ) {
                setError('Choose a future consent expiry within the review validity.')
                return
              }
              const command = crypto.randomUUID()
              void flow.run(
                { kind: 'accept', target: offer.review_id, command, status: 'pending' },
                {
                  command_id: command,
                  review_id: offer.review_id,
                  terms_sha256: offer.terms_sha256,
                  expires_at: end.toISOString(),
                  acknowledge_reviewed_terms: true,
                  acknowledge_consent_only: true,
                }
              )
            }}
          >
            Accept reviewed request
          </button>
          {expired && (
            <p role="alert">This review has expired. Ask the reviewer for a current review.</p>
          )}
        </div>
      )}
      {consent && (
        <div className="space-y-2">
          <p className="break-all">Acceptance reference: {String(flow.view?.acceptance_id)}</p>
          <p>
            Consent: {String(consent.state)}. Share the acceptance reference with the independent
            reviewer.
          </p>
          {consent.state === 'recorded' && (
            <button
              className="btn"
              disabled={flow.locked}
              onClick={() => {
                const command = crypto.randomUUID(),
                  target = id(consent?.consent_id)
                void flow.run(
                  { kind: 'withdraw', target, command, status: 'pending' },
                  { command_id: command, consent_id: target, terms_sha256: consent?.terms_sha256 }
                )
              }}
            >
              Withdraw recorded consent
            </button>
          )}
          <p>
            Consent history and appointment status are separate. Withdrawal confirmation alone does
            not establish that a verified appointment has ended.
          </p>
        </div>
      )}
      <WorkflowHistory flow={flow} />
    </section>
  )
}

export function LegalControlConsentPanel() {
  const { user } = useAuth()
  if (!user || user.authScope !== 'client_vault' || user.tenantId) return null
  return <ConsentDetails key={`${user.id}:${user.authScope}:${user.role}`} />
}
